import React, { useState } from 'react';
import ExtinguisherForm from './components/ExtinguisherForm';
import MapDisplay from './components/MapDisplay';
import AdminDashboard from './components/AdminDashboard';
import LoginForm from './components/LoginForm';
import ConfirmationModal from './components/ConfirmationModal';
import { AuthProvider, useAuth } from './context/AuthContext';
import { userService } from './services/api';
import { Logout, Info } from '@mui/icons-material';
import './App.css';

function AppContent() {
  console.log('🔍 DEBUG: AppContent function called');
  const { user, logout } = useAuth();
  const [showAdmin, setShowAdmin] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const appVersion = process.env.REACT_APP_VERSION || 'unknown';
  const apiUrl = process.env.REACT_APP_API_URL || 'unknown';

  // Fetch user stats when user logs in
  React.useEffect(() => {
    console.log('🔍 DEBUG: User effect triggered, user:', user);
    if (user) {
      const fetchUserStats = async () => {
        try {
          console.log('🔍 DEBUG: Fetching user stats for:', user.username);
          const response = await userService.getCurrentUserStats();
          console.log('🔍 DEBUG: User stats response:', response.data);
          setUserStats(response.data);
        } catch (error) {
          console.error('❌ Failed to fetch user stats:', error);
          // Don't show error to user, just log it
        }
      };
      fetchUserStats();
    } else {
      console.log('🔍 DEBUG: No user, clearing stats');
      setUserStats(null);
    }
  }, [user]);

  // Log when component mounts
  React.useEffect(() => {
    console.log('🔍 DEBUG: AppContent component mounted');
  }, []);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown')) {
        setShowProfileDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileDropdown]);

  const handleLogout = () => {
    setShowProfileDropdown(false);
    setShowLogoutConfirm(true);
  };

  const handleAbout = () => {
    setShowProfileDropdown(false);
    setShowAboutDialog(true);
  };

  const closeAboutDialog = () => {
    setShowAboutDialog(false);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const refreshUserStats = async () => {
    if (user) {
      try {
        const response = await userService.getCurrentUserStats();
        setUserStats(response.data);
      } catch (error) {
        console.error('Failed to refresh user stats:', error);
      }
    }
  };

  return (
    <div className="App">
      <header className="header">
        <div className="container">
          <h1>🔥 FireWatch</h1>
          <p>
            Fire Extinguisher Tracking System<br />
            <span style={{ fontSize: '0.85em', color: '#666' }}>
              Version {appVersion} • API: <span 
                style={{ 
                  color: '#0066cc', 
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => {
                  // Copy only the basic public test command to clipboard
                  const basicCommand = `curl -s "${apiUrl}/api/extinguishers?lat=40.7&lng=-74.0" | python3 -m json.tool | head -30`;
                  
                  // But show all commands in the dialog
                  const loginTest = `curl -s -X POST "${apiUrl}/api/login" -H "Content-Type: application/json" -d '{"username":"admin","password":"admin"}' | python3 -m json.tool`;
                  const authTest = `curl -s "${apiUrl}/api/admin/extinguishers" -H "Authorization: Bearer YOUR_JWT_TOKEN" | python3 -m json.tool`;
                  
                  navigator.clipboard.writeText(basicCommand).then(() => {
                    alert(`✅ Basic API test command copied to clipboard!\n\n📋 COPIED:\n${basicCommand}\n\n🔐 Additional commands for reference:\n\n• Login (get JWT token):\n${loginTest}\n\n• Test authenticated endpoint:\n${authTest}`);
                  }).catch(() => {
                    alert(`API endpoint: ${apiUrl}\n\n📋 Basic test:\n${basicCommand}\n\n🔐 Login test:\n${loginTest}\n\n🔒 Authenticated test:\n${authTest}`);
                  });
                }}
                title="Click to copy API test command"
              >
                {apiUrl}
              </span>
            </span>
          </p>

          {user && (
            <div className="user-info" style={{ marginTop: '1.5rem' }}>
              {/* Single row: Account icon, username, stats, and admin toggle */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '1rem',
                width: '100%'
              }}>
                {/* Account icon */}
                <div className="profile-dropdown" style={{ position: 'relative', display: 'inline-block' }}>
                  <button 
                    className="profile-button"
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    style={{
                      background: '#6c757d',
                      border: '2px solid #6c757d',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      color: 'white',
                      fontSize: '0.8rem',
                      transition: 'all 0.15s ease',
                      fontWeight: 'bold',
                      fontFamily: 'monospace',
                      position: 'relative',
                      overflow: 'hidden',
                      outline: 'none',
                      WebkitTapHighlightColor: 'transparent',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      boxShadow: 'none'
                    }}
                    onMouseDown={(e) => {
                      // Prevent default button behavior
                      e.preventDefault();
                      
                      // Create ripple effect
                      const button = e.currentTarget;
                      const circle = document.createElement('span');
                      const diameter = Math.max(button.clientWidth, button.clientHeight);
                      const radius = diameter / 2;
                      
                      const rect = button.getBoundingClientRect();
                      circle.style.width = circle.style.height = `${diameter}px`;
                      circle.style.left = `${e.clientX - rect.left - radius}px`;
                      circle.style.top = `${e.clientY - rect.top - radius}px`;
                      circle.classList.add('ripple');
                      
                      const ripple = button.getElementsByClassName('ripple')[0];
                      if (ripple) {
                        ripple.remove();
                      }
                      
                      button.appendChild(circle);
                      
                      // Add ripple styles
                      circle.style.position = 'absolute';
                      circle.style.borderRadius = '50%';
                      circle.style.transform = 'scale(0)';
                      circle.style.animation = 'ripple 600ms linear';
                      circle.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                      circle.style.pointerEvents = 'none';
                      
                      // Add keyframes for ripple animation if not already added
                      if (!document.querySelector('#ripple-keyframes')) {
                        const style = document.createElement('style');
                        style.id = 'ripple-keyframes';
                        style.innerHTML = `
                          @keyframes ripple {
                            to {
                              transform: scale(4);
                              opacity: 0;
                            }
                          }
                          
                          .profile-button:focus,
                          .profile-button:active,
                          .profile-button:hover:focus,
                          .profile-button:hover:active {
                            outline: none !important;
                            box-shadow: none !important;
                            border: 2px solid #6c757d !important;
                          }
                        `;
                        document.head.appendChild(style);
                      }
                      
                      // Remove ripple after animation
                      setTimeout(() => {
                        if (circle && circle.parentNode) {
                          circle.remove();
                        }
                      }, 600);
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#5a6268';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#6c757d';
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = 'none';
                      e.target.style.boxShadow = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.outline = 'none';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      userSelect: 'none',
                      pointerEvents: 'none',
                      gap: '2px'
                    }}>
                      {/* Head - circle */}
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: 'currentColor'
                      }}></div>
                      {/* Body - rounded rectangle */}
                      <div style={{
                        width: '14px',
                        height: '10px',
                        backgroundColor: 'currentColor',
                        borderRadius: '7px 7px 2px 2px'
                      }}></div>
                    </div>
                  </button>
                  
                  {showProfileDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '0',
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      minWidth: '150px',
                      zIndex: 1000,
                      marginTop: '0.5rem',
                      overflow: 'hidden'
                    }}>
                      {/* Username Header - Distinct styling */}
                      <div style={{
                        padding: '0.75rem 1rem',
                        borderBottom: '1px solid #dee2e6',
                        fontSize: '0.875rem',
                        color: '#495057',
                        fontWeight: '600',
                        backgroundColor: '#f8f9fa',
                        textAlign: 'center',
                        borderRadius: '3px 3px 0 0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          userSelect: 'none',
                          gap: '1px'
                        }}>
                          {/* Head - circle */}
                          <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: 'currentColor'
                          }}></div>
                          {/* Body - rounded rectangle */}
                          <div style={{
                            width: '10px',
                            height: '7px',
                            backgroundColor: 'currentColor',
                            borderRadius: '5px 5px 1px 1px'
                          }}></div>
                        </div>
                        {user.username}
                      </div>
                      
                      {/* Menu Area - Different styling */}
                      <div style={{ backgroundColor: 'white' }}>
                        <button
                          onClick={handleAbout}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: '#6c757d',
                            transition: 'background-color 0.2s',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <Info sx={{ fontSize: '1rem', color: '#6c757d' }} /> About
                        </button>
                        <button
                          onClick={handleLogout}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: '#6c757d',
                            transition: 'background-color 0.2s',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <Logout sx={{ fontSize: '1rem', color: '#6c757d' }} /> Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Username and stats */}
                <div style={{ textAlign: 'left', flex: '1' }}>
                  <span>{user.username}</span>
                  {userStats && (
                    <div style={{ 
                      fontSize: '0.85em', 
                      color: '#6c757d', 
                      marginTop: '0.25rem'
                    }}>
                      {userStats.weeklyReports} {userStats.weeklyReports === 1 ? 'report' : 'reports'} this week,<br />
                      {userStats.totalReports} total
                    </div>
                  )}
                </div>
                
                {/* Admin toggle button - only for admins */}
                {user.role === 'admin' && (
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setShowAdmin(!showAdmin)}
                    style={{ 
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      lineHeight: '1.2',
                      padding: '0.5rem 0.75rem',
                      minWidth: 'auto'
                    }}
                  >
                    <span style={{ fontSize: '0.85em' }}>
                      {showAdmin ? 'Public' : 'Admin'}
                    </span>
                    <span style={{ fontSize: '0.85em' }}>
                      {showAdmin ? 'View' : 'Dashboard'}
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="container">
        {!user ? (
          <LoginForm />
        ) : showAdmin && user.role === 'admin' ? (
          <AdminDashboard />
        ) : (
          <div className="main-content">
            <div className="form-section">
              <h2>Report Fire Extinguisher</h2>
              <ExtinguisherForm onSuccess={refreshUserStats} />
            </div>
            <div className="map-section">
              <MapDisplay />
            </div>
          </div>
        )}
      </div>

      {/* Custom Logout Confirmation Dialog */}
      <ConfirmationModal
        isVisible={showLogoutConfirm}
        title="🔥 FireWatch"
        message="Logout?"
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
      />

      {/* About FireWatch Dialog */}
      <ConfirmationModal
        isVisible={showAboutDialog}
        title="About FireWatch"
        message={
          <div style={{ textAlign: 'center', lineHeight: '1.6' }}>
            <div style={{ fontSize: '1.1em', marginBottom: '1rem' }}>
              🔥 <strong>FireWatch</strong>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              Fire Extinguisher Tracking System
            </div>
            <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '1rem' }}>
              Version {appVersion}
            </div>
            <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '1rem' }}>
              API: <span 
                style={{ 
                  color: '#0066cc', 
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => {
                  const basicCommand = `curl -s "${apiUrl}/api/extinguishers?lat=40.7&lng=-74.0" | python3 -m json.tool | head -30`;
                  navigator.clipboard.writeText(basicCommand).then(() => {
                    alert(`✅ API test command copied to clipboard!\n\n📋 COPIED:\n${basicCommand}`);
                  }).catch(() => {
                    alert(`API endpoint: ${apiUrl}\n\n📋 Basic test:\n${basicCommand}`);
                  });
                }}
                title="Click to copy API test command"
              >
                {apiUrl}
              </span>
            </div>
            <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '0.5rem', lineHeight: '1.3' }}>
              Developed by{' '}
              <a 
                href="https://www.justek.us/" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#0066cc', textDecoration: 'underline' }}
              >
                Justek, LLC
              </a>
              <br />
              for{' '}
              <a 
                href="https://firecode.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#0066cc', textDecoration: 'underline' }}
              >
                Firecode Safety Equipment, Inc.
              </a>
            </div>
            <div style={{ fontSize: '0.85em', color: '#888' }}>
              © 2025 FireWatch. All rights reserved.
            </div>
          </div>
        }
        onConfirm={closeAboutDialog}
        onCancel={closeAboutDialog}
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;